import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import axios from 'axios';
import { Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Booking = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    activity: 'windsurfing',
    date: '',
    level: 'beginner',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        language
      });
      setStatus({ type: 'success', message: t('booking.success') });
      setFormData({
        name: '',
        email: '',
        phone: '',
        activity: 'windsurfing',
        date: '',
        level: 'beginner',
        message: ''
      });
    } catch (error) {
      setStatus({ type: 'error', message: t('booking.error') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="booking-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-r from-violet-600 to-purple-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('booking.title')}</h1>
          <p className="text-xl text-violet-100">{t('booking.subtitle')}</p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.name')}</label>
                <input
                  type="text"
                  name="name"
                  data-testid="booking-name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.email')}</label>
                <input
                  type="email"
                  name="email"
                  data-testid="booking-email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('booking.phone')}</label>
              <input
                type="tel"
                name="phone"
                data-testid="booking-phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('booking.activity')}</label>
                <select
                  name="activity"
                  data-testid="booking-activity"
                  value={formData.activity}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="windsurfing">{t('nav.windsurfing')}</option>
                  <option value="kitesurfing">{t('nav.kitesurfing')}</option>
                  <option value="wingfoiling">{t('nav.wingfoiling')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('booking.date')}</label>
                <input
                  type="date"
                  name="date"
                  data-testid="booking-date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('booking.level')}</label>
              <select
                name="level"
                data-testid="booking-level"
                value={formData.level}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              >
                <option value="beginner">{t('booking.level_beginner')}</option>
                <option value="intermediate">{t('booking.level_intermediate')}</option>
                <option value="advanced">{t('booking.level_advanced')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('booking.additional_info')}</label>
              <textarea
                name="message"
                data-testid="booking-message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>

            {status.message && (
              <div
                data-testid="booking-status"
                className={`p-4 rounded-lg ${
                  status.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}
              >
                {status.message}
              </div>
            )}

            <button
              type="submit"
              data-testid="booking-submit"
              disabled={loading}
              className="w-full bg-violet-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('booking.submitting') : t('booking.submit')}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Booking;
